import { useEffect, useState, useRef, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/images";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageCircle, Send, Search, Loader2, Paperclip, Download, FileText, FileSpreadsheet, File as FileIcon, Trash2 } from "lucide-react";

const ACCEPT = "image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt";

function fileMeta(ext) {
  const e = (ext || "").toLowerCase();
  if (["xls", "xlsx", "csv"].includes(e)) return { Icon: FileSpreadsheet, cls: "text-emerald-600" };
  if (["pdf", "doc", "docx", "txt"].includes(e)) return { Icon: FileText, cls: "text-red-500" };
  return { Icon: FileIcon, cls: "text-slate-500" };
}

function Attachment({ m }) {
  const url = fileUrl(m.attachment_url);
  if (!url) return null;
  if (m.attachment_kind === "image")
    return <a href={url} target="_blank" rel="noreferrer" data-testid="chat-attach-image"><img src={url} alt={m.attachment_name || "görsel"} className="max-h-56 max-w-full rounded-lg object-cover" /></a>;
  if (m.attachment_kind === "video")
    return <video src={url} controls data-testid="chat-attach-video" className="max-h-56 max-w-full rounded-lg" />;
  if (m.attachment_kind === "audio")
    return <audio src={url} controls data-testid="chat-attach-audio" className="w-56 max-w-full" />;
  const { Icon, cls } = fileMeta(m.attachment_ext);
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="chat-attach-file"
      className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50">
      <Icon className={`h-6 w-6 shrink-0 ${cls}`} />
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{m.attachment_name || `dosya.${m.attachment_ext || ""}`}</span>
      <Download className="h-4 w-4 shrink-0 text-slate-400" />
    </a>
  );
}

export default function Chat() {
  const { user: me } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [peer, setPeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const loadContacts = useCallback(() => {
    api.get("/chat/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, []);
  const loadMessages = useCallback((p) => {
    if (!p) return;
    api.get(`/chat/messages?peer=${encodeURIComponent(p.email)}`).then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadContacts(); const t = setInterval(loadContacts, 15000); return () => clearInterval(t); }, [loadContacts]);
  useEffect(() => {
    if (!peer) return;
    loadMessages(peer);
    const t = setInterval(() => loadMessages(peer), 5000);
    return () => clearInterval(t);
  }, [peer, loadMessages]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openPeer = (c) => {
    setPeer(c); setMessages([]);
    setContacts((cs) => cs.map((x) => (x.email === c.email ? { ...x, unread: 0 } : x)));
  };

  const send = async () => {
    const body = text.trim();
    if (!body || !peer) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/messages", { to_email: peer.email, body });
      setMessages((m) => [...m, data]); setText(""); loadContacts();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const clearChat = async () => {
    if (!peer) return;
    if (!window.confirm(`${peer.name} ile sohbet SİZİN görünümünüzden temizlenecek. Karşı taraf etkilenmez. Devam edilsin mi?`)) return;
    setClearing(true);
    try {
      const { data } = await api.delete(`/chat/messages?peer=${encodeURIComponent(peer.email)}`);
      setMessages([]); loadContacts();
      toast.success("Sohbet temizlendi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setClearing(false); }
  };

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !peer) return;
    if (f.size > 25 * 1024 * 1024) return toast.error("Dosya 25MB'den büyük olamaz");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data: up } = await api.post("/chat/upload", fd);
      const { data } = await api.post("/chat/messages", {
        to_email: peer.email, body: text.trim(),
        attachment_url: up.url, attachment_name: up.filename, attachment_ext: up.ext,
        attachment_kind: up.kind, attachment_size: up.size,
      });
      setMessages((m) => [...m, data]); setText(""); loadContacts();
      toast.success("Dosya gönderildi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const filtered = contacts.filter((c) => !q ||
    (c.name || "").toLowerCase().includes(q.toLowerCase()) ||
    (c.email || "").toLowerCase().includes(q.toLowerCase()));
  const hhmm = (s) => (s ? String(s).slice(11, 16) : "");

  return (
    <div className="fade-in" data-testid="chat-page">
      <div className="mb-4 flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-brand-green" />
        <div><p className="overline">İletişim</p><h1 className="font-heading text-2xl font-bold text-slate-900">Mesajlar</h1></div>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[320px_1fr]">
        {/* Contacts */}
        <div className="rounded-lg bg-white ring-1 ring-inset ring-slate-200">
          <div className="border-b border-slate-100 p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input data-testid="chat-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kişi ara..." className="pl-9" />
            </div>
          </div>
          <div className="max-h-[62vh] overflow-y-auto">
            {filtered.length === 0 && <p className="p-6 text-center text-sm text-slate-400">Mesajlaşabileceğiniz kişi yok.</p>}
            {filtered.map((c) => (
              <button key={c.email} data-testid={`chat-contact-${c.email}`} onClick={() => openPeer(c)}
                className={`flex w-full items-center gap-3 border-b border-slate-50 p-3 text-left transition-colors hover:bg-slate-50 ${peer?.email === c.email ? "bg-emerald-50/60" : ""}`}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-green text-xs font-bold text-white">{(c.name || "?").slice(0, 1)}</div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{c.name}</p>
                  <p className="truncate text-[11px] text-slate-400">{c.last_body || c.role_label}</p>
                </div>
                {c.unread > 0 && <span data-testid={`chat-unread-${c.email}`} className="rounded-full bg-brand-orange px-1.5 py-0.5 text-[10px] font-bold text-white">{c.unread}</span>}
              </button>
            ))}
          </div>
        </div>
        {/* Conversation */}
        <div className="flex min-h-[62vh] flex-col rounded-lg bg-white ring-1 ring-inset ring-slate-200">
          {!peer ? (
            <div className="flex flex-1 flex-col items-center justify-center text-slate-300">
              <MessageCircle className="h-10 w-10" /><p className="mt-2 text-sm">Mesajlaşmak için bir kişi seçin</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-slate-100 p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-green text-xs font-bold text-white">{(peer.name || "?").slice(0, 1)}</div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{peer.name}</p>
                  <p className="truncate text-[11px] text-slate-400">{peer.role_label}{peer.location_names?.length ? " · " + peer.location_names.join(", ") : ""}</p>
                </div>
                <Button data-testid="chat-clear-btn" onClick={clearChat} disabled={clearing} variant="outline" size="sm" className="shrink-0 text-slate-600" title="Sohbeti temizle">
                  {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1 h-4 w-4" /> Temizle</>}
                </Button>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto p-4" style={{ maxHeight: "52vh" }} data-testid="chat-thread">
                {messages.length === 0 && <p className="text-center text-xs text-slate-400">Henüz mesaj yok. İlk mesajı gönderin.</p>}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] space-y-1.5 rounded-2xl px-3 py-2 text-sm ${m.mine ? "bg-brand-green text-white" : "bg-slate-100 text-slate-800"}`}>
                      {m.attachment_url && <Attachment m={m} />}
                      {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
                      <p className={`text-right text-[10px] ${m.mine ? "text-white/70" : "text-slate-400"}`}>{hhmm(m.created_at)}</p>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="flex items-center gap-2 border-t border-slate-100 p-3">
                <input ref={fileRef} type="file" accept={ACCEPT} onChange={onFile} className="hidden" data-testid="chat-file-input" />
                <Button data-testid="chat-attach-btn" onClick={onPickFile} disabled={uploading} variant="outline" size="icon" className="shrink-0" title="Dosya / görsel / video / ses ekle">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                </Button>
                <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Mesaj yazın veya dosya ekleyin..." className="flex-1" />
                <Button data-testid="chat-send" onClick={send} disabled={sending || !text.trim()} className="bg-brand-green text-white hover:bg-brand-green-600">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
